import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Phone } from "lucide-react";

import { CtaBand, PageHero } from "@/components/page-parts";
import { BUSINESS_LOCATIONS } from "@/lib/business-locations";
import { FIRM, FIRM_ADDRESS } from "@/lib/firm";

const description =
  "Business sale, purchase and commercial conveyancing lawyers for Golden Beach, Caloundra, Pelican Waters, Kings Beach, Currimundi, Little Mountain and Baringa.";

const collectionSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Business lawyers on the Caloundra coast",
  description,
  isPartOf: { "@type": "WebSite", name: FIRM.name },
  hasPart: BUSINESS_LOCATIONS.map((l) => ({
    "@type": "WebPage",
    name: l.title,
    url: `https://coastsidelaw.com.au/business-lawyers/${l.slug}`,
  })),
};

export const Route = createFileRoute("/business-lawyers/")({
  head: () => ({
    meta: [
      { title: fitTitle(`Business Sale & Commercial Lawyers, Caloundra Coast | ${FIRM.name}`) },
      { name: "description", content: fitDescription(description) },
      { property: "og:title", content: fitTitle(`Business lawyers, Caloundra and Golden Beach | ${FIRM.name}`) },
      { property: "og:description", content: fitDescription(description) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/business-lawyers" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/business-lawyers" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(collectionSchema) }],
  }),
  component: BusinessLawyersIndex,
});

function BusinessLawyersIndex() {
  return (
    <>
      <PageHero
        personSlug="michael-klein"
        eyebrow="Business & commercial"
        title="Business sales and commercial conveyancing, suburb by suburb"
        lead={`From our office at ${FIRM_ADDRESS} we act for business owners buying, selling and leasing across the Caloundra coast. Pick your area for the issues that come up locally.`}
      />

      <section className="mx-auto max-w-5xl px-5 py-16">
        <ul className="grid gap-4 sm:grid-cols-2">
          {BUSINESS_LOCATIONS.map((l) => (
            <li key={l.slug}>
              <Link
                to="/business-lawyers/$suburb"
                params={{ suburb: l.slug }}
                className="group flex h-full flex-col rounded-sm border border-border bg-card p-6 transition-colors hover:border-primary"
              >
                <span className="font-serif text-xl text-foreground group-hover:text-primary">
                  {l.name}
                </span>
                <span className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {l.sectors.slice(0, 3).join(" · ")}
                </span>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                  Business lawyers in {l.name}
                  <ArrowRight className="size-4" aria-hidden="true" />
                </span>
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-12 rounded-sm border border-border bg-secondary/40 p-6">
          <h2 className="font-serif text-2xl text-foreground">Before you sign anything</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Download our Queensland business sale and purchase checklist, or send us the contract
            and we will quote a fixed fee to review it.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/business-sale-checklist"
              className="inline-flex items-center justify-center rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              Get the checklist
            </Link>
            <a
              href={FIRM.phoneHref}
              className="inline-flex items-center gap-2 rounded-sm border border-border px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
            >
              <Phone className="size-4 text-primary" aria-hidden="true" />
              Call {FIRM.phone}
            </a>
          </div>
        </div>
      </section>

      <CtaBand
        title="Buying or selling a business on the Coast?"
        body="Tell us where the deal is up to and we will tell you what needs to happen next — and what it will cost."
      />
    </>
  );
}
